import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"

import { cn } from "@/utils/utils"
import { BLOCKING, DIALOG_TITLE, DIALOG_WIDTHS, ICON_BUTTON, SCRIM, type DialogWidth } from "@/components/ui/recipes"
import { XIcon } from "@phosphor-icons/react"

// Lureo's one dialog: a blocking card over the scrim, its header bar (title, description, close), its body, and its
// footer bar of actions on the right. Every dialog is these four parts; nothing restyles them.

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

/** How far the scrim dims the page: default for most dialogs; dim for a question before an action (publish, or just test?); soft, lighter and blurred, for the publish, share and test flows. */
const SCRIMS = { default: "", dim: "bg-lureo-shade/60", soft: "bg-lureo-shade/40 backdrop-blur-lureo-blur-sm" } as const

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      SCRIM,
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:ease-lureo-easing-enter data-[state=closed]:ease-lureo-easing-exit data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    width?: DialogWidth
    hideCloseButton?: boolean
    /** Where it portals: another window's body (the detached timeline) instead of this one's. */
    container?: HTMLElement | null
    scrim?: keyof typeof SCRIMS
  }
>(({ className, children, width = "md", hideCloseButton, container, scrim = "default", onInteractOutside, ...props }, ref) => (
  <DialogPortal container={container}>
    <DialogOverlay className={SCRIMS[scrim]} />
    <DialogPrimitive.Content
      ref={ref}
      onInteractOutside={(event) => {
        // Answering the cookie banner, or picking one of Google's place suggestions (drawn on the body, outside the
        // dialog), is not a click away from the dialog
        if ((event.target as Element | null)?.closest?.('[data-cookie-consent], .pac-container')) event.preventDefault();
        onInteractOutside?.(event);
      }}
      className={cn(
        "fixed left-[50%] top-[50%] z-lureo-layer-overlay flex max-h-[90vh] w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-lureo-radius-dialog font-lureo-font-ui duration-lureo-duration-moderate data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:ease-lureo-easing-enter data-[state=closed]:ease-lureo-easing-exit data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-3 data-[state=closed]:slide-out-to-bottom-3",
        BLOCKING,
        DIALOG_WIDTHS[width],
        className
      )}
      {...props}
    >
      {children}
      {!hideCloseButton && (
        // In a dialog with no body it sits in the corner, as a question's close does
        <DialogPrimitive.Close className={cn(ICON_BUTTON.dialog, "absolute right-4 top-3 [[data-slot=dialog-header]:has(+[data-slot=dialog-footer])~&]:right-2 [[data-slot=dialog-header]:has(+[data-slot=dialog-footer])~&]:top-2")}>
          <XIcon className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/** The header bar: the title and an optional description, clear of the close button; with no body, the top of one unlined block. */
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="dialog-header"
    className={cn(
      "flex min-h-14 shrink-0 flex-col justify-center gap-1.5 border-b-lureo-border-width border-lureo-line py-3 pl-6 pr-14 text-left [&:has(+[data-slot=dialog-footer])]:min-h-0 [&:has(+[data-slot=dialog-footer])]:border-b-0 [&:has(+[data-slot=dialog-footer])]:pb-0 [&:has(+[data-slot=dialog-footer])]:pt-6",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

/** The dialog's content, scrolling when it is taller than the screen allows. */
const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5",
      className
    )}
    {...props}
  />
)
DialogBody.displayName = "DialogBody"

/** The footer bar: the actions, on the right, the primary last; split puts its first child (Back) on the left; with no body, the bottom of one unlined block. */
const DialogFooter = ({
  className,
  split = false,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { split?: boolean }) => (
  <div
    data-slot="dialog-footer"
    className={cn(
      // Straight after the header (a dialog with no body) it has no line, and the dialog's padding below it
      "flex shrink-0 gap-2 border-t-lureo-border-width border-lureo-line px-6 py-3 [[data-slot=dialog-header]+&]:border-t-0 [[data-slot=dialog-header]+&]:pb-6 [[data-slot=dialog-header]+&]:pt-4",
      split ? "flex-row items-center justify-between" : "flex-col-reverse sm:flex-row sm:items-center sm:justify-end",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(DIALOG_TITLE, className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-lureo-14 text-lureo-ink-subtle leading-5", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
